<template>
  <div v-if="user.id" class="user-navigation" ref="userMenu">
    <NavigationServiceAlerts v-if="isServiceAlertsEnabled" />

    <span class="avatar">{{ user.initials }}</span>
    <div class="flex flex-col">
      <span class="font-normal">{{ user.username }}</span>
      <span class="text-sm text-gray-500">{{ user.role !== undefined ? $t(ROLE_TRANSLATIONS[user.role]) : '' }}</span>      
      <span class="max-w-[16rem] truncate text-[8px] mt-[5px] text-gray-500" :title="user.employerName ?? ''">{{ user.employerName ?? '' }}</span>
    </div>

    <Button data-testid="user-button" @click="onclickMenu" :icon="`${showMenu ? 'dirDown' : 'dirUp'}`" variant="ghost"
      size="l" class="transition-all" hide-label />

    <transition name="submenu-transition">
      <div v-show="showMenu" class="user-dropdown" :style="{ top: `${topOffset}px`, right: `${rightOffset}px` }">
        <div class="menu">
          <div v-if="!user.isUser" class="mt-4 w-full">
            <NuxtLink :to="!route.path.includes('/admin') ? adminRoute : userRoute"
              :key="!route.path.includes('/admin') ? adminLabel : userLabel" class="flex items-center justify-start">
              <Icon icon="users" color="brand" />
              {{ !route.path.includes('/admin') ? adminLabel : userLabel }}
            </NuxtLink>
          </div>

          <NavigationLocaleSwitch />

          <div class="mt-4 w-full text-right">
            <NuxtLink v-for="link in links" :to="link.url" :key="link.label" class="flex items-center justify-end">
              <Icon icon="logout" color="brand" />
              <span class="font-bold">{{ link.label }}</span>
            </NuxtLink>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { Button, Icon } from '@skillstown/leap';
import { useUser } from '~~/stores/user';
import { Role } from '@/data/enums/Role';
import { HTMLHelper } from '~~/helpers/HTMLHelper';
import { i18n } from '@/plugins/i18n';

const user = useUser();
const route = useRoute();
const isServiceAlertsEnabled = useConfiguration().FEATURE_SERVICE_ALERTS;

const userMenu = ref();
const showMenu = ref(false);

const adminLabel = computed(() => {
  if (!route.path.includes('/admin')) {
    if (user.role === Role.EmployerAdmin || user.role === Role.TripleAdmin || user.role === Role.TripleaReadonlyAdmin) {
      return i18n.global.t('to_admin_panel');
    } else if (user.role === Role.Viewer) {
      return i18n.global.t('to_teams_overview');
    } else if (user.role === Role.Author) {
      return i18n.global.t('to_article_manager');
    }
  }
});

const userLabel = computed(() => i18n.global.t('to_user_panel'));
const userRoute = '/dashboard';
const adminRoute = computed(() => {
  if (!route.path.includes('/admin')) {
    if (user.role === Role.EmployerAdmin || user.role === Role.TripleAdmin || user.role === Role.TripleaReadonlyAdmin) {
      return '/admin/articles';
    } else if (user.role === Role.Viewer) {
      return '/admin/teams';
    } else if (user.role === Role.Author) {
      return '/admin/articles';
    }
  }
});
const links = computed(() => [
  {
    label: i18n.global.t('logout'),
    url: '/logout',
  },
]);

const ROLE_TRANSLATIONS: Record<Role, string> = {
  [Role.User]: 'user',
  [Role.Viewer]: 'viewer',
  [Role.Author]: 'author',
  [Role.EmployerAdmin]: 'employer_admin',
  [Role.TripleAdmin]: 'triple_admin',
  [Role.TripleaReadonlyAdmin]: 'readonly_admin'
};

onMounted(() => {
  document.addEventListener('click', onFocusOut);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', onFocusOut);
});

function onclickMenu(): void {
  userDropdownPosition();
  nextTick(() => (showMenu.value = !showMenu.value));
}

const topOffset = ref(0);
const rightOffset = ref(0);

function userDropdownPosition(): void {
  const parent = document.querySelector('.user-navigation');

  if (!parent) {
    topOffset.value = 0;
    return;
  }

  const offset = HTMLHelper.getElementPositionOnScreen(parent);

  // 52 + 25 dropdown margin
  topOffset.value = Math.floor(offset.top + 52);
}

function onFocusOut(event: MouseEvent) {
  if (!showMenu.value) {
    return;
  }

  if (!HTMLHelper.isDescendant(userMenu.value, event.target as HTMLElement)) {
    showMenu.value = false;
  }
}
</script>

<style scoped lang="postcss">
.user-navigation {
  @apply relative flex items-center;
  margin-left: var(--size-4);
  padding-left: var(--size-4);
}

.avatar {
  @apply block rounded-full text-center text-sm font-semibold;
  background-color: var(--color-brand);
  color: var(--color-white);
  width: var(--size-7);
  height: var(--size-7);
  line-height: var(--size-7);
  margin-right: var(--size-4);
}

.user-dropdown {
  @apply absolute z-30 mt-3 w-screen max-w-[275px] transform overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 sm:px-0;
}

.menu {
  @apply relative grid gap-2 bg-white p-6;

  a {
    color: var(--color-grey-pure);
    font-size: var(--font-size-xs);

    &:hover {
      color: var(--color-brand);
    }
  }
}
</style>
